/**
 * PledgeProgressTracker - Embeddable progress widget for campaigns
 * Features: Thermometer, donut chart, stats, and live pledge feed
 */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Heart, TrendingUp, Users, Target, ArrowUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== ANIMATED COUNTER ==============
const AnimatedNumber = ({ value, prefix = '$', duration = 1500 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  
  useEffect(() => {
    const start = display;
    const diff = value - start;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  
  return <span>{prefix}{display.toLocaleString()}</span>;
};

// ============== THERMOMETER ==============
const Thermometer = ({ percentage, raised, goal, color = '#10b981' }) => {
  const [animatedPct, setAnimatedPct] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedPct(Math.min(percentage, 100)), 300);
    return () => clearTimeout(timer);
  }, [percentage]);
  
  return (
    <div className="flex flex-col items-center gap-4" data-testid="thermometer-widget">
      {/* Thermometer body */}
      <div className="relative w-16 h-64 bg-slate-100 rounded-full overflow-hidden border-2 border-slate-200 shadow-inner">
        {/* Mercury fill */}
        <div
          className="absolute bottom-0 left-0 right-0 rounded-full transition-all duration-[2000ms] ease-out"
          style={{
            height: `${animatedPct}%`,
            background: `linear-gradient(to top, ${color}, ${color}dd)`,
            boxShadow: `0 0 20px ${color}40`,
          }}
        />
        {/* Glow effect at top of mercury */}
        {animatedPct > 5 && (
          <div
            className="absolute left-1 right-1 h-3 rounded-full transition-all duration-[2000ms]"
            style={{
              bottom: `calc(${animatedPct}% - 6px)`,
              background: `linear-gradient(to top, transparent, ${color}60)`,
            }}
          />
        )}
        {/* Percentage markers */}
        {[25, 50, 75].map(mark => (
          <div key={mark} className="absolute left-0 right-0 flex items-center" style={{ bottom: `${mark}%` }}>
            <div className="w-2 h-px bg-slate-300" />
            <span className="text-[9px] text-slate-400 ml-auto mr-1">{mark}%</span>
          </div>
        ))}
      </div>
      {/* Bulb */}
      <div
        className="w-24 h-24 rounded-full flex items-center justify-center -mt-6 border-2 border-slate-200 shadow-lg"
        style={{ background: `radial-gradient(circle at 40% 40%, ${color}cc, ${color})` }}
      >
        <div className="text-white text-center">
          <div className="text-lg font-bold">{Math.round(percentage)}%</div>
          <div className="text-[10px] opacity-80">raised</div>
        </div>
      </div>
      {/* Labels */}
      <div className="text-center">
        <p className="text-2xl font-bold text-slate-900"><AnimatedNumber value={raised} /></p>
        <p className="text-sm text-slate-500">of <span className="font-medium">${goal.toLocaleString()}</span> goal</p>
      </div>
    </div>
  );
};

// ============== DONUT CHART ==============
const DonutChart = ({ percentage, raised, goal, color = '#10b981', size = 200 }) => {
  const [animatedPct, setAnimatedPct] = useState(0);
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedPct(Math.min(percentage, 100)), 300);
    return () => clearTimeout(timer);
  }, [percentage]);
  
  const offset = circumference - (animatedPct / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center gap-4" data-testid="donut-chart-widget">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke="#e2e8f0" strokeWidth="12"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={color} strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-[2000ms] ease-out"
            style={{ filter: `drop-shadow(0 0 6px ${color}60)` }}
          />
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-slate-900">{Math.round(animatedPct)}%</span>
          <span className="text-xs text-slate-500">funded</span>
        </div>
      </div>
      <div className="text-center">
        <p className="text-2xl font-bold text-slate-900"><AnimatedNumber value={raised} /></p>
        <p className="text-sm text-slate-500">raised of <span className="font-medium">${goal.toLocaleString()}</span></p>
      </div>
    </div>
  );
};

// ============== PROGRESS BAR ==============
const ProgressBar = ({ percentage, raised, goal, color = '#10b981' }) => {
  const [animatedPct, setAnimatedPct] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedPct(Math.min(percentage, 100)), 300);
    return () => clearTimeout(timer);
  }, [percentage]);
  
  return (
    <div className="w-full space-y-3" data-testid="progress-bar-widget">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-3xl font-bold text-slate-900"><AnimatedNumber value={raised} /></p>
          <p className="text-sm text-slate-500">raised of ${goal.toLocaleString()} goal</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold" style={{ color }}>{Math.round(animatedPct)}%</p>
        </div>
      </div>
      {/* Bar */}
      <div className="relative h-6 bg-slate-100 rounded-full overflow-hidden shadow-inner">
        <div
          className="h-full rounded-full transition-all duration-[2000ms] ease-out flex items-center justify-end pr-2"
          style={{
            width: `${Math.max(animatedPct, 2)}%`,
            background: `linear-gradient(90deg, ${color}90, ${color})`,
            boxShadow: `0 2px 8px ${color}40`,
          }}
        >
          {animatedPct > 15 && (
            <span className="text-[10px] font-bold text-white">{Math.round(animatedPct)}%</span>
          )}
        </div>
      </div>
      {/* Milestones */}
      <div className="flex justify-between text-[10px] text-slate-400 px-1">
        <span>$0</span>
        <span>${Math.round(goal * 0.25).toLocaleString()}</span>
        <span>${Math.round(goal * 0.5).toLocaleString()}</span>
        <span>${Math.round(goal * 0.75).toLocaleString()}</span>
        <span>${goal.toLocaleString()}</span>
      </div>
    </div>
  );
};

// ============== MAIN TRACKER COMPONENT ==============
const PledgeProgressTracker = ({ slug, style = 'thermometer', color = '#10b981', showPledges = true, compact = false }) => {
  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-public', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}`);
      if (!res.ok) throw new Error('Campaign not found');
      return res.json();
    },
    refetchInterval: 30000,
  });

  const { data: pledgesData } = useQuery({
    queryKey: ['campaign-pledges-public', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/pledges/public`);
      if (!res.ok) return { pledges: [], summary: {} };
      return res.json();
    },
    refetchInterval: 30000,
    enabled: showPledges,
  });

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-8 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!campaign) return null;

  const goal = campaign.goal_amount || campaign.target_amount || 10000;
  const raised = campaign.raised_amount || campaign.current_amount || pledgesData?.summary?.total_pledged || 0;
  const percentage = goal > 0 ? (raised / goal) * 100 : 0;
  const pledgeCount = pledgesData?.summary?.pledge_count || 0;
  const recentPledges = (pledgesData?.pledges || []).slice(0, 5);

  const Widget = style === 'thermometer' ? Thermometer 
    : style === 'donut' ? DonutChart 
    : ProgressBar;

  if (compact) {
    return (
      <Card className="overflow-hidden" data-testid="pledge-tracker-compact">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <DonutChart percentage={percentage} raised={raised} goal={goal} color={color} size={80} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-slate-900 truncate">{campaign.name}</h3>
              <div className="flex gap-3 mt-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{pledgeCount}</span>
                <span className="flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" />{Math.round(percentage)}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="pledge-progress-tracker">
      <CardContent className="p-6">
        {/* Campaign name */}
        <div className="text-center mb-6">
          <Badge variant="outline" className="mb-2 text-xs" style={{ borderColor: color, color }}>
            <Heart className="h-3 w-3 mr-1" fill={color} /> Fundraising
          </Badge>
          <h2 className="text-xl font-bold text-slate-900">{campaign.name}</h2>
        </div>

        {/* Visualization */}
        <div className="flex justify-center mb-6">
          <Widget percentage={percentage} raised={raised} goal={goal} color={color} />
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <Users className="h-4 w-4 mx-auto mb-1 text-slate-500" />
            <p className="text-lg font-bold text-slate-900">{pledgeCount}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Pledgers</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <Target className="h-4 w-4 mx-auto mb-1 text-slate-500" />
            <p className="text-lg font-bold text-slate-900">${goal.toLocaleString()}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Goal</p>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <ArrowUp className="h-4 w-4 mx-auto mb-1" style={{ color }} />
            <p className="text-lg font-bold" style={{ color }}>${raised.toLocaleString()}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Raised</p>
          </div>
        </div>

        {/* Recent pledges feed */}
        {showPledges && recentPledges.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Recent Pledges</p>
            <div className="space-y-2">
              {recentPledges.map((p, i) => (
                <div key={p.id || i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: color }}>
                      {(p.donor_name || 'A')[0].toUpperCase()}
                    </div>
                    <span className="text-sm text-slate-700">{p.donor_name}</span>
                  </div>
                  <span className="text-sm font-bold" style={{ color }}>${p.amount?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pledge CTA */}
        <a
          href={`/campaigns/${slug}`}
          className="block mt-4 text-center py-3 rounded-lg font-medium text-white transition-all hover:opacity-90"
          style={{ background: color }}
          data-testid="pledge-now-cta"
        >
          Pledge Now
        </a>
      </CardContent>
    </Card>
  );
};

export { PledgeProgressTracker, Thermometer, DonutChart, ProgressBar, AnimatedNumber };
export default PledgeProgressTracker;
